import { Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAPICall, useAppSelector } from '../../hooks';
import { AdminToast, ToastProvider } from '../../ui/components';
import { AddParticipantDashboard } from '../dashboard/team-list/team-detail/add-participant/[teamId]';
import CampaignService from '../../sdk/services/campaignService';
import { DynamicObjectInterface } from '../../sdk/type';
import { dateTimeToCustomFormat } from '../../ui/utils';
import Error from '../../assets/image/icons/ico_error.svg';
import ScratchGame from '../game/scratchGame/ScratchGame';
import { AdminMenu } from './menus';
import styles from './Admin.module.scss';
import { AdminDashboard } from './admin-dashboard/admin-dashboard';
import { AdminUserEditPage, AdminUserList } from './user-setup';
import { AdminRewardEditPage, AdminRewardList } from './reward-setup';
import { AdminLocationList } from './location-setup';
import { AdminCampaignList, AdminCampaignEditPage } from './campaign-configuration';
import { AdminTeamsList, AdminTeamEditPage } from './teams';
// import { AddParticipant } from './teams/edit/add-participant/[teamId]';
import { AdminParticipantDetails } from './teams/edit/participant-details/[partiId]';
import { AdminGeneralParticipantList } from './participants';
import { AdminGeneralCoachList } from './coaches';
import { AdminGeneralCoachDetails } from './coaches/edit/[coachId]';
import { RequiredAuth } from './RequiredAuth';
import { Report } from './report';
import { BulkUpload } from './bulk-upload';
import { AdminGeneralInventoryList, AdminInventoryEditPage } from './inventory';
import { AdminGeneralRedemptionList, AdminGeneralRedemptionEdit } from './redemption';

interface filteredCampaignData {
  campaignName: string;
  dataRemovalDate: string;
}

export const AdminHome = () => {
  
  const adminType = useAppSelector((state) => Number(state.auth.userInfo?.adminType ?? 0));
  const [campaignData, setCampaignData] = useState<filteredCampaignData>();
  const apiCall = useAPICall();
  let location = useLocation();

  const isUnauthorized =
    (adminType === 2 &&
      (location.pathname.startsWith('/admin/promotion') ||
      location.pathname.startsWith('/admin/master'))) ||
    (adminType === 3 &&
      (location.pathname.startsWith('/admin/challenge') ||
      location.pathname.startsWith('/admin/master'))) ||
     (adminType === 3 &&
      (location.pathname.startsWith('/admin/promotion/report') ||
      location.pathname.startsWith('/admin/promotion/bulk-upload'))) ;

  useEffect(() => {
    (async () => {
      const res = await apiCall(CampaignService?.getCampaigns);
      let campaignData = res?.data?.data ?? [];

      campaignData = campaignData?.filter(
        (i: DynamicObjectInterface) => i.status !== 'DELETED' && i.status !== 'CLOSED' && !!i.dataRemovalDate,
      );

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const filteredCampaignData = campaignData?.find((i: DynamicObjectInterface) => {
        const removalDate = new Date(i.dataRemovalDate);
        removalDate.setHours(0, 0, 0, 0);

        const notificationStart = new Date(removalDate.getTime() - 15 * 86400000); // 15 days before
        return today >= notificationStart && today < removalDate;
      });
      setCampaignData(filteredCampaignData);
    })();
  }, []);

  return (
    <RequiredAuth>
      <div className={styles['adminPortal']}>
        <ToastProvider>
          <AdminToast timeout={100000} topPosition="100px">
            <AdminMenu adminType={adminType} />
            {isUnauthorized ? (
              <div style={{ padding: 24 , width: '80%'  }}>
                <h1 style={{ color: 'red'  }} >401 – Permission Denied</h1>
                <p>You do not have access to this page.</p>
              </div>
            ): (
              <div className={styles['adminContent']}>
                {!!campaignData && !window.location.href.includes('edit') && (
                  <div className={styles['adminPortal-notification']}>
                    <img src={Error} alt={`Warning`} width={'32px'} height={'32px'} className={styles['warning']} />
                    <div>
                      Note: All users' health data for {campaignData.campaignName} will be removed by{' '}
                      {dateTimeToCustomFormat(campaignData.dataRemovalDate, 'DD/MM/yyyy')}. Please download all required
                      data before this date.
                    </div>
                  </div>
                )}

                <Routes>
                  <Route path="/master/user-setup/edit" element={<AdminUserEditPage />} />
                  <Route path="/master/user-setup/list" element={<AdminUserList />} />
                  <Route path="/master/location-setup/list" element={<AdminLocationList />} />
                  <Route path="/master/reward-setup/list" element={<AdminRewardList />} />
                  <Route path="/master/reward-setup/edit" element={<AdminRewardEditPage />} />
                  <Route path="/master/campaign-configuration/list" element={<AdminCampaignList />} />
                  <Route path="/master/campaign-configuration/edit" element={<AdminCampaignEditPage />} />
                  <Route path="/challenge/manage-team/list" element={<AdminTeamsList />} />
                  <Route path="/challenge/manage-team/edit" element={<AdminTeamEditPage />} />
                  <Route path="/challenge/manage-team/edit/add-participant" element={<AddParticipantDashboard isAdmin />} />
                  <Route path="/challenge/manage-team/edit/participant-details" element={<AdminParticipantDetails />} />
                  <Route path="/challenge/manage-participant/list" element={<AdminGeneralParticipantList />} />
                  <Route path="/challenge/manage-participant/edit" element={<AdminParticipantDetails />} />
                  <Route path="/challenge/manage-coach/list" element={<AdminGeneralCoachList />} />
                  <Route path="/challenge/manage-coach/edit" element={<AdminGeneralCoachDetails />} />
                  <Route path="/promotion/manage-inventory/list" element={<AdminGeneralInventoryList adminType={adminType} />} />
                  <Route path="/promotion/manage-inventory/edit" element={<AdminInventoryEditPage adminType={adminType}/>} />
                  <Route path="/promotion/manage-redemption/list" element={<AdminGeneralRedemptionList />} />
                  <Route path="/promotion/manage-redemption/edit" element={<AdminGeneralRedemptionEdit adminType={adminType} />} />
                  <Route path="/promotion/manage-redemption/new" element={<ScratchGame />} />
                  <Route path="/challenge/report/team" element={<Report title="Report - Participants" campaignType="CHALLENGE" name="team" />} />
                  <Route
                    path="/challenge/report/team-leader-group"
                    element={<Report title="Report - Team Leader (Group)" campaignType="CHALLENGE" name="coach-group" />}
                  />
                  <Route path="/promotion/report/inventory" element={<Report title="Report - Inventory" campaignType="PROMOTION" name="inventory" />} />
                  <Route path="/promotion/report/redemption" element={<Report title="Report - Redemption" campaignType="PROMOTION" name="promo-redemption" />} />
                  <Route
                    path="/promotion/bulk-upload/inventory"
                    element={<BulkUpload title="Inventory" campaignType="PROMOTION"  name="inventory-list" />}
                  />
                  <Route path="/" element={<AdminDashboard />} />
                </Routes>
              </div>
            )}
          </AdminToast>
        </ToastProvider>
      </div>
    </RequiredAuth>
  );
};
